import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Link,
  Badge,
  IconButton,
  Tooltip,
  Collapse,
  Grid,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  TextField,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { getAllFixVersions, getWorkItems, getProductionBugs, getInternalBugs, generateReleaseNotes, fetchWorkItemsByFixVersion, getPullRequestsByFixVersion, get, syncAdoQueryResults, getReleaseInternalBugs } from '../../services/apiService';
import { getApiUrl } from '../../services/configService';
import { isAuthenticated, isAdmin } from '../../services/authService';
import { generateSummary } from '../../utils/textSummarizer';
import { formatDate } from '../../utils/formatters';
import FeatureChangesTable from '../releases/FeatureChangesTable';
import BugsTable from '../releases/BugsTable';
import ReleaseNotesEditor from '../releases/ReleaseNotesEditor';
import Loading from '../common/Loading';
import ReleaseHealthCharts from '../releases/ReleaseHealthCharts';
import BugComparisonCharts from '../quality-assurance/BugComparisonCharts';
import ReleasesTab from '../quality-assurance/ReleasesTab';
import QAReportTab from '../quality-assurance/QAReportTab';
import ReleaseSignOffTab from '../releases/ReleaseSignOffTab';
import ReleaseStatusTab from '../releases/ReleaseStatusTab';

/**
 * PublicReleasesView component for displaying work items by release (fix version)
 * Public-facing component with authentication-based features
 * @returns {JSX.Element} PublicReleasesView component
 */
const PublicReleasesView = () => {
  const [searchParams] = useSearchParams();
  // State for fix versions
  const [fixVersions, setFixVersions] = useState([]);
  const [fixVersionDetails, setFixVersionDetails] = useState({});
  const [selectedFixVersion, setSelectedFixVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for work items
  const [workItems, setWorkItems] = useState([]);
  const [productionBugs, setProductionBugs] = useState([]);
  const [internalBugs, setInternalBugs] = useState([]);
  const [loadingWorkItems, setLoadingWorkItems] = useState(false);
  const [fetchingLatest, setFetchingLatest] = useState(false);
  
  // State for PRs
  const [pullRequests, setPullRequests] = useState([]);
  const [prsByWorkItem, setPrsByWorkItem] = useState({});
  const [loadingPRs, setLoadingPRs] = useState(false);
  const [prDialogOpen, setPrDialogOpen] = useState(false);
  const [selectedWorkItemForPRs, setSelectedWorkItemForPRs] = useState(null);
  
  // State for repository dialog
  const [repoDialogOpen, setRepoDialogOpen] = useState(false);
  const [repositories, setRepositories] = useState([]);
  
  // State for collapsible sections
  const [featureChangesExpanded, setFeatureChangesExpanded] = useState(true);
  const [productionBugsExpanded, setProductionBugsExpanded] = useState(true);
  const [internalBugsExpanded, setInternalBugsExpanded] = useState(true);
  
  // State for filters (shared across all sections)
  const [workItemTypeFilter, setWorkItemTypeFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [showOnlyWithPRs, setShowOnlyWithPRs] = useState(false);
  const [showItemsWithoutParent, setShowItemsWithoutParent] = useState(false);
  
  // State for release notes
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [releaseNotesEditorOpen, setReleaseNotesEditorOpen] = useState(false);
  const [releaseNotesItems, setReleaseNotesItems] = useState([]);
  const [generatingReleaseNotes, setGeneratingReleaseNotes] = useState(false);
  
  // State for tabs
  const [tabValue, setTabValue] = useState(0);
  
  // Internal Bugs tab (ADO query sync)
  const [adoQueryUrl, setAdoQueryUrl] = useState('');
  const [adoQueryLoading, setAdoQueryLoading] = useState(false);
  const [adoQueryError, setAdoQueryError] = useState(null);
  const [adoQueryData, setAdoQueryData] = useState(null); // { columns: string[], rows: Array<Object> }
  const [adoQuerySortColumn, setAdoQuerySortColumn] = useState(null);
  const [adoQuerySortDirection, setAdoQuerySortDirection] = useState('asc');
  const [adoShowWithoutParent, setAdoShowWithoutParent] = useState(false);
  const [adoShowWithoutFixVersion, setAdoShowWithoutFixVersion] = useState(false);
  const [adoShowWithoutIssueSource, setAdoShowWithoutIssueSource] = useState(false);
  const [adoShowWithoutEnvironment, setAdoShowWithoutEnvironment] = useState(false);
  const [adoExcludeClosed, setAdoExcludeClosed] = useState(false);
  
  // Traceability tab removed - functionality merged with QA Report
  
  // Check authentication - make it reactive
  const [isAuth, setIsAuth] = useState(false);
  
  // Update auth status on mount and when it might change
  useEffect(() => {
    const checkAuth = () => {
      setIsAuth(isAuthenticated());
    };
    
    // Check immediately
    checkAuth();
    
    // Check periodically (every 30 seconds) to catch token expiration
    const authInterval = setInterval(checkAuth, 30000);
    
    // Also check on storage events (when token is set/removed in another tab)
    window.addEventListener('storage', checkAuth);
    
    return () => {
      clearInterval(authInterval);
      window.removeEventListener('storage', checkAuth);
    };
  }, []);
  
  // Filter fix versions to only include those with format 5.9.08 and above
  const isValidFixVersion = (version) => {
    const regex = /^(\d+)\.(\d+)\.(\d+)$/;
    const match = version.match(regex);
    if (!match) return false;
    
    const [, major, minor, patch] = match;
    return (
      parseInt(major) > 5 || 
      (parseInt(major) === 5 && parseInt(minor) > 9) || 
      (parseInt(major) === 5 && parseInt(minor) === 9 && parseInt(patch) >= 8)
    );
  };
  
  // Sort versions to find the most recent (highest version number)
  const sortVersions = (versions) => {
    return [...versions].sort((a, b) => {
      const regex = /^(\d+)\.(\d+)\.(\d+)$/;
      const matchA = a.match(regex);
      const matchB = b.match(regex);
      
      if (!matchA || !matchB) return 0;
      
      const [, majorA, minorA, patchA] = matchA.map(Number);
      const [, majorB, minorB, patchB] = matchB.map(Number);
      
      if (majorA !== majorB) return majorB - majorA;
      if (minorA !== minorB) return minorB - minorA;
      return patchB - patchA;
    });
  };

  // Normalize version string for comparison (e.g. "5.10.0" and "5.10.00" match)
  const normalizeVersionForMatch = (v) => {
    if (!v || typeof v !== 'string') return '';
    const parts = v.trim().split('.').map((p) => parseInt(p, 10));
    if (parts.some((n) => Number.isNaN(n))) return v.trim();
    while (parts.length < 3) parts.push(0);
    return `${parts[0]}.${parts[1]}.${String(parts[2]).padStart(2, '0')}`;
  };

  // Find version from list that matches URL release (exact or normalized match)
  const findMatchingVersion = (releaseFromUrl, versionList) => {
    if (!releaseFromUrl || !versionList || versionList.length === 0) return null;
    const normalized = normalizeVersionForMatch(releaseFromUrl);
    const exact = versionList.find((ver) => ver === releaseFromUrl);
    if (exact) return exact;
    return versionList.find((ver) => normalizeVersionForMatch(ver) === normalized) || null;
  };

  // Filtered fix versions
  const filteredFixVersions = useMemo(() => {
    const filtered = fixVersions.filter(isValidFixVersion);
    return sortVersions(filtered);
  }, [fixVersions]);

  // When URL has ?release= (e.g. from Release Calendar "Release Content" link), select that release
  useEffect(() => {
    const releaseFromUrl = searchParams.get('release');
    if (!releaseFromUrl || filteredFixVersions.length === 0) return;
    const match = findMatchingVersion(releaseFromUrl, filteredFixVersions);
    if (match) setSelectedFixVersion(match);
  }, [searchParams, filteredFixVersions]);

  // Fetch fix versions on component mount
  useEffect(() => {
    const fetchFixVersions = async () => {
      try {
        setLoading(true);
        const response = await getAllFixVersions();
        
        let versionStrings = [];
        let versionDetailsMap = {};
        
        if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          const activeVersionsData = response.data.data.filter(fixVersion => fixVersion.active);
          versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
          activeVersionsData.forEach(fixVersion => {
            const id = fixVersion._id ? (typeof fixVersion._id === 'string' ? fixVersion._id : fixVersion._id.toString()) : null;
            versionDetailsMap[fixVersion.version] = {
              ...fixVersion,
              _id: id,
              // Use the PDF URL from the response if available, otherwise construct it
              releaseNotesPdfUrl: fixVersion.releaseNotesPdfUrl || (id ? `/api/fix-versions/${id}/release-notes-pdf` : null)
            };
          });
        } else if (response && response.data && Array.isArray(response.data)) {
          const activeVersionsData = response.data.filter(fixVersion => fixVersion.active);
          versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
          activeVersionsData.forEach(fixVersion => {
            const id = fixVersion._id ? (typeof fixVersion._id === 'string' ? fixVersion._id : fixVersion._id.toString()) : null;
            versionDetailsMap[fixVersion.version] = {
              ...fixVersion,
              _id: id,
              // Use the PDF URL from the response if available, otherwise construct it
              releaseNotesPdfUrl: fixVersion.releaseNotesPdfUrl || (id ? `/api/fix-versions/${id}/release-notes-pdf` : null)
            };
          });
        } else if (response && Array.isArray(response)) {
          const activeVersionsData = response.filter(fixVersion => fixVersion.active);
          versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
          activeVersionsData.forEach(fixVersion => {
            const id = fixVersion._id ? (typeof fixVersion._id === 'string' ? fixVersion._id : fixVersion._id.toString()) : null;
            versionDetailsMap[fixVersion.version] = {
              ...fixVersion,
              _id: id,
              // Use the PDF URL from the response if available, otherwise construct it
              releaseNotesPdfUrl: fixVersion.releaseNotesPdfUrl || (id ? `/api/fix-versions/${id}/release-notes-pdf` : null)
            };
          });
        } else if (response && response.success && Array.isArray(response.data)) {
          const activeVersionsData = response.data.filter(fixVersion => fixVersion.active);
          versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
          activeVersionsData.forEach(fixVersion => {
            const id = fixVersion._id ? (typeof fixVersion._id === 'string' ? fixVersion._id : fixVersion._id.toString()) : null;
            versionDetailsMap[fixVersion.version] = {
              ...fixVersion,
              _id: id,
              // Use the PDF URL from the response if available, otherwise construct it
              releaseNotesPdfUrl: fixVersion.releaseNotesPdfUrl || (id ? `/api/fix-versions/${id}/release-notes-pdf` : null)
            };
          });
        } else {
          setError('Failed to load fix versions. Invalid response format.');
          setFixVersions([]);
          setFixVersionDetails({});
          return;
        }
        
        setFixVersions(versionStrings);
        setFixVersionDetails(versionDetailsMap);
        
        // Auto-select: prefer release from URL (?section=releases&release=5.9.21), else most recent
        const filtered = versionStrings.filter(isValidFixVersion);
        if (filtered.length > 0) {
          const sorted = sortVersions(filtered);
          const releaseFromUrl = new URLSearchParams(window.location.search).get('release');
          const match = releaseFromUrl ? findMatchingVersion(releaseFromUrl, filtered) : null;
          const initial = match || sorted[0];
          setSelectedFixVersion(prev => prev || initial);
        }
      } catch (err) {
        console.error('Error fetching fix versions:', err);
        setError('Failed to load fix versions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFixVersions();
  }, []);

  // Load persisted release internal bugs (ADO URL and last result) on mount
  useEffect(() => {
    getReleaseInternalBugs()
      .then((data) => {
        if (data.queryUrl) setAdoQueryUrl(data.queryUrl);
        if (data.columns && data.rows && (data.columns.length > 0 || data.rows.length > 0)) {
          setAdoQueryData({ columns: data.columns, rows: data.rows });
        }
      })
      .catch(() => {});
  }, []);

  // Fetch work items and PRs when fix version changes
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedFixVersion) {
        setWorkItems([]);
        setProductionBugs([]);
        setInternalBugs([]);
        setPullRequests([]);
        setPrsByWorkItem({});
        return;
      }
      
      try {
        setLoadingWorkItems(true);
        setError(null);
        
        // Fetch work items, production bugs, and internal bugs in parallel (public access)
        const [workItemsResponse, productionBugsResponse, internalBugsResponse] = await Promise.all([
          get('/api/workitems/public', { fixVersion: selectedFixVersion }),
          get('/api/workitems/production-bugs/public', { fixVersion: selectedFixVersion }),
          get('/api/workitems/internal-bugs/public', { fixVersion: selectedFixVersion })
        ]);
        
        // Process work items (including bugs)
        if (workItemsResponse && workItemsResponse.data) {
          if (Array.isArray(workItemsResponse.data)) {
            setWorkItems(workItemsResponse.data);
          } else if (workItemsResponse.data.success && Array.isArray(workItemsResponse.data.data)) {
            setWorkItems(workItemsResponse.data.data);
          } else if (Array.isArray(workItemsResponse.data.data)) {
            setWorkItems(workItemsResponse.data.data);
          } else {
            setWorkItems([]);
          }
        } else {
          setWorkItems([]);
        }
        
        // Process production bugs
        if (productionBugsResponse && productionBugsResponse.data) {
          if (Array.isArray(productionBugsResponse.data)) {
            setProductionBugs(productionBugsResponse.data);
          } else if (productionBugsResponse.data.success && Array.isArray(productionBugsResponse.data.data)) {
            setProductionBugs(productionBugsResponse.data.data);
          } else if (Array.isArray(productionBugsResponse.data.data)) {
            setProductionBugs(productionBugsResponse.data.data);
          } else {
            setProductionBugs([]);
          }
        } else {
          setProductionBugs([]);
        }
        
        // Process internal bugs
        if (internalBugsResponse && internalBugsResponse.data) {
          if (Array.isArray(internalBugsResponse.data)) {
            setInternalBugs(internalBugsResponse.data);
          } else if (internalBugsResponse.data.success && Array.isArray(internalBugsResponse.data.data)) {
            setInternalBugs(internalBugsResponse.data.data);
          } else if (Array.isArray(internalBugsResponse.data.data)) {
            setInternalBugs(internalBugsResponse.data.data);
          } else {
            setInternalBugs([]);
          }
        } else {
          setInternalBugs([]);
        }
        
        // Fetch PRs
        await fetchPRs();
        
        // Refresh fix version details to get updated PDF URL
        const fixVersionsResponse = await getAllFixVersions();
        if (fixVersionsResponse && fixVersionsResponse.data) {
          let versionDetailsMap = {};
          let activeVersionsData = [];
          
          if (fixVersionsResponse.data.data && Array.isArray(fixVersionsResponse.data.data)) {
            activeVersionsData = fixVersionsResponse.data.data.filter(fv => fv.active);
          } else if (Array.isArray(fixVersionsResponse.data)) {
            activeVersionsData = fixVersionsResponse.data.filter(fv => fv.active);
          } else if (Array.isArray(fixVersionsResponse)) {
            activeVersionsData = fixVersionsResponse.filter(fv => fv.active);
          }
          
          activeVersionsData.forEach(fixVersion => {
            if (fixVersion.version === selectedFixVersion) {
              versionDetailsMap[fixVersion.version] = {
                ...fixVersion,
                releaseNotesPdfUrl: fixVersion.releaseNotesPdfUrl || (fixVersion._id ? `/api/fix-versions/${fixVersion._id}/release-notes-pdf` : null)
              };
            }
          });
          
          if (Object.keys(versionDetailsMap).length > 0) {
            setFixVersionDetails(prev => ({
              ...prev,
              ...versionDetailsMap
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching work items:', err);
        setError('Failed to load work items. Please try again later.');
      } finally {
        setLoadingWorkItems(false);
      }
    };
    
    fetchData();
  }, [selectedFixVersion]);
  
  // Fetch PRs for the selected fix version
  const fetchPRs = async () => {
    if (!selectedFixVersion) return;
    
    try {
      setLoadingPRs(true);
      const response = await getPullRequestsByFixVersion(selectedFixVersion);
      
      if (response && response.success && response.data) {
        setPullRequests(response.data.pullRequests || []);
        setPrsByWorkItem(response.data.prsByWorkItem || {});
      } else {
        setPullRequests([]);
        setPrsByWorkItem({});
      }
    } catch (err) {
      console.error('Error fetching PRs:', err);
      setPullRequests([]);
      setPrsByWorkItem({});
    } finally {
      setLoadingPRs(false);
    }
  };
  
  // Handle fix version change
  const handleFixVersionChange = (event) => {
    setSelectedFixVersion(event.target.value);
  };
  
  // Handle get latest from ADO
  const handleGetLatest = async () => {
    if (!selectedFixVersion) {
      setSnackbarMessage('Please select a release first');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setFetchingLatest(true);
      setError(null);
      
      const response = await fetchWorkItemsByFixVersion(selectedFixVersion, true);
      
      if (response && response.success) {
        setSnackbarMessage(
          `Successfully fetched ${response.data.total} work items (${response.data.created} created, ${response.data.updated} updated). PRs have been saved to database.`
        );
        setSnackbarOpen(true);
        
        // Refresh work items after fetching (public access)
        const [workItemsResponse, productionBugsResponse, internalBugsResponse] = await Promise.all([
          get('/api/workitems/public', { fixVersion: selectedFixVersion }),
          get('/api/workitems/production-bugs/public', { fixVersion: selectedFixVersion }),
          get('/api/workitems/internal-bugs/public', { fixVersion: selectedFixVersion })
        ]);
        
        // Process responses (including bugs)
        if (workItemsResponse && workItemsResponse.data) {
          if (Array.isArray(workItemsResponse.data)) {
            setWorkItems(workItemsResponse.data);
          } else if (workItemsResponse.data.success && Array.isArray(workItemsResponse.data.data)) {
            setWorkItems(workItemsResponse.data.data);
          } else if (Array.isArray(workItemsResponse.data.data)) {
            setWorkItems(workItemsResponse.data.data);
          } else {
            setWorkItems([]);
          }
        } else {
          setWorkItems([]);
        }
        
        if (productionBugsResponse && productionBugsResponse.data) {
          if (Array.isArray(productionBugsResponse.data)) {
            setProductionBugs(productionBugsResponse.data);
          } else if (productionBugsResponse.data.success && Array.isArray(productionBugsResponse.data.data)) {
            setProductionBugs(productionBugsResponse.data.data);
          } else if (Array.isArray(productionBugsResponse.data.data)) {
            setProductionBugs(productionBugsResponse.data.data);
          } else {
            setProductionBugs([]);
          }
        } else {
          setProductionBugs([]);
        }
        
        if (internalBugsResponse && internalBugsResponse.data) {
          if (Array.isArray(internalBugsResponse.data)) {
            setInternalBugs(internalBugsResponse.data);
          } else if (internalBugsResponse.data.success && Array.isArray(internalBugsResponse.data.data)) {
            setInternalBugs(internalBugsResponse.data.data);
          } else if (Array.isArray(internalBugsResponse.data.data)) {
            setInternalBugs(internalBugsResponse.data.data);
          } else {
            setInternalBugs([]);
          }
        } else {
          setInternalBugs([]);
        }
        
        // Refresh PRs from database (they were saved during fetchWorkItemsByFixVersion)
        await fetchPRs();
      } else {
        throw new Error(response?.message || 'Failed to fetch work items');
      }
    } catch (error) {
      console.error('Error fetching latest work items:', error);
      setError(error.message || 'Failed to fetch work items from ADO. Please try again later.');
      setSnackbarMessage(error.message || 'Failed to fetch work items from ADO. Please try again later.');
      setSnackbarOpen(true);
    } finally {
      setFetchingLatest(false);
    }
  };
  
  // Function to organize and filter work items (same logic as in FeatureChangesTable)
  const organizeWorkItems = (items, targetFixVersion) => {
    const result = [];
    
    // Group items by their type (exclude bugs - they're shown separately)
    const epics = items.filter(item => item.workItemType === 'Epic');
    const features = items.filter(item => item.workItemType === 'Feature');
    const pbis = items.filter(item => 
      item.workItemType === 'Product Backlog Item' || 
      item.workItemType === 'Task'
    );
    
    // Rule 1: If Epic fix version matches, list just Epic
    const matchingEpics = epics.filter(epic => 
      epic.fixVersion === targetFixVersion
    );
    
    // Store matching epic IDs for later use
    const matchingEpicIds = matchingEpics.map(epic => epic.id);
    
    matchingEpics.forEach(epic => {
      result.push({
        ...epic,
        level: 'Epic',
        children: []
      });
    });
    
    // Rule 2: If Feature fix version matches but its epic fix version doesn't match, list just Feature
    const epicIds = matchingEpics.map(epic => epic.id);
    const matchingFeatures = features.filter(feature => 
      feature.fixVersion === targetFixVersion && 
      !epicIds.includes(feature.parent)
    );
    
    matchingFeatures.forEach(feature => {
      result.push({
        ...feature,
        level: 'Feature',
        children: []
      });
    });
    
    // Rule 3: If Epic and Feature don't match, and only PBI fix version matches, list only PBI
    const featureIds = matchingFeatures.map(feature => feature.id);
    
    // Find PBIs that match the fix version AND:
    // 1. Don't have a parent that is a matching feature (to avoid showing PBIs under matching features)
    // 2. Don't have a parent that is a feature whose parent is a matching epic (to avoid showing PBIs under features that are under matching epics)
    const matchingPbis = pbis.filter(pbi => {
      // PBI must match the fix version
      if (pbi.fixVersion !== targetFixVersion) return false;
      
      // If PBI's parent is a matching feature, don't include it
      if (featureIds.includes(pbi.parent)) return false;
      
      // If PBI's parent is a feature whose parent is a matching epic, don't include it
      const parentFeature = features.find(f => f.id === pbi.parent);
      if (parentFeature && matchingEpicIds.includes(parentFeature.parent)) return false;
      
      // Otherwise, include the PBI
      return true;
    });
    
    matchingPbis.forEach(pbi => {
      result.push({
        ...pbi,
        level: 'PBI',
        children: []
      });
    });
    
    return result;
  };
  
  // Handle generate release notes
  const handleGenerateReleaseNotes = () => {
    if (!isAuth) {
      setSnackbarMessage('Please log in to generate release notes');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setLoadingWorkItems(true);
      
      // Get the filtered work items using the same logic as FeatureChangesTable
      const nonBugWorkItems = workItems.filter(item => item.workItemType !== 'Bug');
      const filteredWorkItems = organizeWorkItems(nonBugWorkItems, selectedFixVersion);
      
      // Combine bugs from ProductionBug collection and WorkItem collection where issueSource == 'Production'
      const bugsFromWorkItemsProduction = workItems.filter(item => 
        item.workItemType === 'Bug' && 
        (item.issueSource === 'Production' || item.issueSource?.toLowerCase() === 'production')
      );
      const bugMapProduction = new Map();
      productionBugs.forEach(bug => {
        const id = bug.id?.toString() || bug._id?.toString();
        if (id && !bugMapProduction.has(id)) {
          bugMapProduction.set(id, bug);
        }
      });
      bugsFromWorkItemsProduction.forEach(bug => {
        const id = bug.id?.toString() || bug._id?.toString();
        if (id && !bugMapProduction.has(id)) {
          bugMapProduction.set(id, bug);
        }
      });
      const allProductionBugs = Array.from(bugMapProduction.values());
      
      // Combine bugs from InternalBug collection and WorkItem collection where issueSource != 'Production'
      const bugsFromWorkItemsInternal = workItems.filter(item => 
        item.workItemType === 'Bug' && 
        item.issueSource !== 'Production' && 
        item.issueSource?.toLowerCase() !== 'production'
      );
      const bugMapInternal = new Map();
      internalBugs.forEach(bug => {
        const id = bug.id?.toString() || bug._id?.toString();
        if (id && !bugMapInternal.has(id)) {
          bugMapInternal.set(id, bug);
        }
      });
      bugsFromWorkItemsInternal.forEach(bug => {
        const id = bug.id?.toString() || bug._id?.toString();
        if (id && !bugMapInternal.has(id)) {
          bugMapInternal.set(id, bug);
        }
      });
      const allInternalBugs = Array.from(bugMapInternal.values());
      
      // Combine filtered work items with bugs, adding a source field to track where each bug came from
      const allItems = [
        ...filteredWorkItems,
        ...allProductionBugs.map(bug => ({ ...bug, bugSource: 'production' })),
        ...allInternalBugs.map(bug => ({ ...bug, bugSource: 'internal' }))
      ];
      
      // Generate summaries for each item to show in the editor
      const itemsWithSummaries = allItems.map(item => {
        // Create a copy of the item with a generated summary
        return {
          ...item,
          summary: generateSummary(item)
        };
      });
      
      setReleaseNotesItems(itemsWithSummaries);
      setReleaseNotesEditorOpen(true);
    } catch (error) {
      console.error('Error preparing release notes:', error);
      setSnackbarMessage('Failed to prepare release notes. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoadingWorkItems(false);
    }
  };
  
  // Handle approve release notes
  const handleApproveReleaseNotes = async (items) => {
    try {
      setGeneratingReleaseNotes(true);
      
      // Get the fix version ID
      const fixVersionId = fixVersionDetails[selectedFixVersion]?._id;
      
      if (!fixVersionId) {
        console.error('Fix version ID not found for version:', selectedFixVersion);
        console.error('Available fix version details:', fixVersionDetails);
        throw new Error(`Fix version ID not found for version: ${selectedFixVersion}`);
      }
      
      console.log(`Generating release notes for fix version: ${selectedFixVersion}, ID: ${fixVersionId}`);
      
      // Send items to backend to generate HTML and PDF
      // Include version string in body as primary identifier (more reliable than ID)
      const idToUse = fixVersionId || 'unknown';
      const response = await generateReleaseNotes(idToUse, items, selectedFixVersion);
      
      if (response && response.success) {
        // If response contains a blob (PDF), trigger download
        if (response.blob) {
          const url = window.URL.createObjectURL(response.blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `release_notes_${selectedFixVersion.replace(/\./g, '_')}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
        
        // Update the fix version details with the PDF URL (PDF is now saved in DB)
        // Refresh fix version details to get the updated PDF URL
        const fixVersionResponse = await getAllFixVersions();
        if (fixVersionResponse && fixVersionResponse.data) {
          let releasesData = [];
          if (fixVersionResponse.data.data && Array.isArray(fixVersionResponse.data.data)) {
            releasesData = fixVersionResponse.data.data;
          } else if (Array.isArray(fixVersionResponse.data)) {
            releasesData = fixVersionResponse.data;
          } else if (Array.isArray(fixVersionResponse)) {
            releasesData = fixVersionResponse;
          } else if (fixVersionResponse.success && Array.isArray(fixVersionResponse.data)) {
            releasesData = fixVersionResponse.data;
          }
          
          const updatedFixVersion = releasesData.find(fv => fv.version === selectedFixVersion);
          if (updatedFixVersion) {
            setFixVersionDetails(prev => ({
              ...prev,
              [selectedFixVersion]: {
                ...prev[selectedFixVersion],
                ...updatedFixVersion, // Include all fields including _id
                releaseNotesPdfUrl: updatedFixVersion.releaseNotesPdfUrl || (updatedFixVersion._id ? `/api/fix-versions/${updatedFixVersion._id.toString()}/release-notes-pdf` : null)
              }
            }));
          }
        }
        
        setSnackbarMessage('Release notes PDF generated and saved successfully!');
        setSnackbarOpen(true);
        setReleaseNotesEditorOpen(false);
      } else {
        throw new Error('Failed to generate release notes');
      }
    } catch (error) {
      console.error('Error generating release notes:', error);
      setSnackbarMessage('Failed to generate release notes. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setGeneratingReleaseNotes(false);
    }
  };
  
  // Get PRs for a specific work item
  const getPRsForWorkItem = (workItemId) => {
    const workItemIdStr = workItemId.toString();
    // Check both direct mapping and search in all PRs
    const directPRs = prsByWorkItem[workItemIdStr] || [];
    
    // Also search in all PRs for mentions of this work item
    const allPRs = pullRequests.filter(pr => {
      const prText = `${pr.title || ''} ${pr.url || ''}`.toLowerCase();
      return prText.includes(`#${workItemIdStr}`) || prText.includes(`ado-${workItemIdStr}`) || prText.includes(`wi${workItemIdStr}`);
    });
    
    // Combine and deduplicate
    const combined = [...directPRs, ...allPRs];
    const unique = combined.filter((pr, index, self) => 
      index === self.findIndex(p => p.number === pr.number && p.repository === pr.repository)
    );
    
    return unique;
  };
  
  // Handle PR count click
  const handlePRCountClick = (workItem) => {
    const prs = getPRsForWorkItem(workItem.id);
    setSelectedWorkItemForPRs({ workItem, prs });
    setPrDialogOpen(true);
  };
  
  // Get PR status color
  const getPRStatusColor = (status) => {
    switch (status) {
      case 'merged':
        return 'success';
      case 'active':
      case 'open':
        return 'primary';
      case 'closed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Calculate unique repositories from PRs
  const uniqueRepositories = useMemo(() => {
    const repos = new Set();
    pullRequests.forEach(pr => {
      if (pr.repository) {
        repos.add(pr.repository);
      }
    });
    return Array.from(repos).sort();
  }, [pullRequests]);
  
  // Calculate relevant PRs for this release
  const relevantPRs = useMemo(() => {
    if (!selectedFixVersion) return [];
    
    return pullRequests.filter(pr => {
      if (!pr.repository || !pr.baseBranch) return false;
      
      const repoName = pr.repository.toLowerCase();
      const isHighqCollaborate = repoName.includes('highq_collaborate');
      
      if (isHighqCollaborate) {
        // For highq_collaborate repos, PR must target release-{fixVersion} branch
        const expectedBranch = `release-${selectedFixVersion}`;
        return pr.baseBranch === expectedBranch;
      } else {
        // For other repos, PR must target default branch (main or master)
        return pr.baseBranch === 'main' || pr.baseBranch === 'master';
      }
    });
  }, [pullRequests, selectedFixVersion]);
  
  // Handle repository count click
  const handleRepositoryCountClick = () => {
    setRepositories(uniqueRepositories);
    setRepoDialogOpen(true);
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSyncAdoQuery = async () => {
    const url = (adoQueryUrl || '').trim();
    if (!url) {
      setAdoQueryError('Please enter an ADO Query URL.');
      return;
    }
    setAdoQueryError(null);
    setAdoQueryLoading(true);
    try {
      const data = await syncAdoQueryResults(url);
      if (data.queryUrl) setAdoQueryUrl(data.queryUrl);
      setAdoQueryData({ columns: data.columns || [], rows: data.rows || [] });
    } catch (err) {
      setAdoQueryError(err.message || 'Failed to sync query results.');
      setAdoQueryData(null);
    } finally {
      setAdoQueryLoading(false);
    }
  };

  // Traceability functionality removed - merged with QA Report tab
  
  // Render Release Content tab (current content)
  const renderReleaseContent = () => (
    <>
      {/* Fix Version Selector - Only in Release Content tab */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="fix-version-select-label">Select Release (Fix Version)</InputLabel>
            <Select
              labelId="fix-version-select-label"
              id="fix-version-select"
              value={selectedFixVersion}
              label="Select Release (Fix Version)"
              onChange={handleFixVersionChange}
            >
              <MenuItem value="">
                <em>Select a release</em>
              </MenuItem>
              {filteredFixVersions.map((version) => (
                <MenuItem key={version} value={version}>
                  {version}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {selectedFixVersion && isAuth && (
            <>
              <Button 
                variant="outlined" 
                color="primary"
                disabled={!selectedFixVersion || fetchingLatest}
                onClick={handleGetLatest}
                startIcon={fetchingLatest ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {fetchingLatest ? 'Fetching...' : 'Fetch Latest from ADO'}
              </Button>
              
              <Button 
                variant="contained" 
                color="primary"
                disabled={!selectedFixVersion || !isValidFixVersion(selectedFixVersion) || loadingWorkItems}
                onClick={handleGenerateReleaseNotes}
              >
                Generate Release Notes
              </Button>
              
              {selectedFixVersion && (() => {
                const fixVersionDetail = fixVersionDetails[selectedFixVersion];
                if (!fixVersionDetail) {
                  console.log('[View Release Notes] Fix version detail not found for:', selectedFixVersion);
                  return null;
                }
                
                // Get the ID and ensure it's a string
                // Handle both ObjectId objects and string IDs
                let fixVersionId = fixVersionDetail._id;
                console.log('[View Release Notes] Raw fixVersionId:', fixVersionId, 'Type:', typeof fixVersionId);
                
                if (!fixVersionId) {
                  console.error('[View Release Notes] Fix version ID is missing. Fix version detail:', fixVersionDetail);
                  return null;
                }
                
                // Convert to string if it's an ObjectId or other object
                if (typeof fixVersionId === 'object' && fixVersionId.toString) {
                  fixVersionId = fixVersionId.toString();
                  console.log('[View Release Notes] Converted ObjectId to string:', fixVersionId);
                } else if (typeof fixVersionId !== 'string') {
                  fixVersionId = String(fixVersionId);
                  console.log('[View Release Notes] Converted to string:', fixVersionId);
                }
                
                // Validate that it's a valid MongoDB ObjectId format (24 hex characters)
                if (!/^[0-9a-fA-F]{24}$/.test(fixVersionId)) {
                  console.error('[View Release Notes] Invalid fix version ID format:', fixVersionId);
                  console.error('[View Release Notes] Fix version detail:', fixVersionDetail);
                  return null;
                }
                
                // Construct the PDF URL using the actual fix version ID
                const pdfUrl = `/api/fix-versions/${fixVersionId}/release-notes-pdf`;
                
                console.log('[View Release Notes] Constructed URLs:');
                console.log('  - pdfUrl (relative):', pdfUrl);
                console.log('  - window.location.origin:', window.location.origin);
                console.log('  - Selected fix version:', selectedFixVersion);
                
                return (
                  <Button
                    variant="outlined"
                    color="primary"
                    sx={{ ml: 1 }}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        console.log('[View Release Notes] Button clicked!');
                        console.log('[View Release Notes] Fix Version ID:', fixVersionId);
                        console.log('[View Release Notes] Fix Version Detail:', fixVersionDetail);
                        
                        // Get the API base URL
                        // For window.open, we need the full URL, not a relative one
                        // In development, use localhost:5001 directly
                        // In production, use the API URL from config or current origin
                        let baseUrl = '';
                        try {
                          if (process.env.NODE_ENV === 'development') {
                            // In development, always use localhost:5001 for window.open
                            baseUrl = 'http://localhost:5001';
                          } else {
                            // In production, try to get API URL from config
                            const apiUrl = await getApiUrl();
                            baseUrl = apiUrl || window.location.origin;
                          }
                        } catch (error) {
                          console.error('[View Release Notes] Error getting API URL:', error);
                          // Fallback: use localhost:5001 in dev, current origin in production
                          baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : window.location.origin;
                        }
                        
                        // Construct the full URL
                        const fullPdfUrl = `${baseUrl}${pdfUrl}`;
                        const urlWithTimestamp = `${fullPdfUrl}?t=${Date.now()}`;
                        
                        console.log('[View Release Notes] Full URL breakdown:');
                        console.log('  - Environment:', process.env.NODE_ENV);
                        console.log('  - API Base URL:', baseUrl);
                        console.log('  - Relative Path:', pdfUrl);
                        console.log('  - Full URL:', fullPdfUrl);
                        console.log('  - Final URL with timestamp:', urlWithTimestamp);
                        console.log('  - window.location.origin:', window.location.origin);
                        
                        // Open PDF directly from server - add timestamp to prevent caching
                        const newWindow = window.open(urlWithTimestamp, '_blank', 'noopener,noreferrer');
                        if (!newWindow) {
                          console.error('[View Release Notes] Pop-up blocked!');
                          setSnackbarMessage('Please allow pop-ups to view the release notes');
                          setSnackbarOpen(true);
                        } else {
                          console.log('[View Release Notes] Window opened successfully with URL:', urlWithTimestamp);
                        }
                      } catch (error) {
                        console.error('[View Release Notes] Error opening PDF:', error);
                        console.error('[View Release Notes] Error stack:', error.stack);
                        setSnackbarMessage(error.message || 'Failed to open release notes. Please try again.');
                        setSnackbarOpen(true);
                      }
                    }}
                  >
                    View Release Notes
                  </Button>
                );
              })()}
            </>
          )}
          
          {selectedFixVersion && (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', ml: 2 }}>
              <Typography variant="body1" sx={{ whiteSpace: 'nowrap', mr: 2 }}>
                <strong>Release Date:</strong> {
                  fixVersionDetails[selectedFixVersion] && 
                  fixVersionDetails[selectedFixVersion].releaseDate ? 
                  formatDate(fixVersionDetails[selectedFixVersion].releaseDate) : 
                  'Not available'
                }
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
      
      {selectedFixVersion ? (
        loadingWorkItems ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Release Summary */}
            <Box mb={4}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Release Summary
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: 2,
                  mt: 2,
                  pb: 1
                }}>
                  <Paper elevation={3} sx={{ p: 2, minWidth: '150px', flex: 1, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {workItems.filter(item => item.workItemType === 'Epic').length}
                    </Typography>
                    <Typography variant="subtitle1">Epics</Typography>
                  </Paper>
                  <Paper elevation={3} sx={{ p: 2, minWidth: '150px', flex: 1, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {workItems.filter(item => item.workItemType === 'Feature').length}
                    </Typography>
                    <Typography variant="subtitle1">Features</Typography>
                  </Paper>
                  <Paper elevation={3} sx={{ p: 2, minWidth: '150px', flex: 1, bgcolor: '#fff8e1', textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {workItems.filter(item => 
                        item.workItemType === 'Product Backlog Item' || 
                        item.workItemType === 'Task'
                      ).length}
                    </Typography>
                    <Typography variant="subtitle1">PBIs</Typography>
                  </Paper>
                  <Paper elevation={3} sx={{ p: 2, minWidth: '150px', flex: 1, bgcolor: '#ffebee', textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">
                      {(() => {
                        // Calculate unique production bugs count (deduplicated by ID)
                        const bugsFromWorkItems = workItems.filter(item => 
                          item.workItemType === 'Bug' && 
                          (item.issueSource === 'Production' || item.issueSource?.toLowerCase() === 'production')
                        );
                        const bugIds = new Set();
                        productionBugs.forEach(bug => {
                          const id = bug.id?.toString() || bug._id?.toString();
                          if (id) bugIds.add(id);
                        });
                        bugsFromWorkItems.forEach(bug => {
                          const id = bug.id?.toString() || bug._id?.toString();
                          if (id) bugIds.add(id);
                        });
                        return bugIds.size;
                      })()}
                    </Typography>
                    <Typography variant="subtitle1">Production Bugs</Typography>
                  </Paper>
                  <Paper elevation={3} sx={{ p: 2, minWidth: '150px', flex: 1, bgcolor: '#f3e5f5', textAlign: 'center' }}>
                    <Typography variant="h4" color="secondary.main">
                      {(() => {
                        // Calculate unique internal bugs count (deduplicated by ID)
                        const bugsFromWorkItems = workItems.filter(item => 
                          item.workItemType === 'Bug' && 
                          item.issueSource !== 'Production' && 
                          item.issueSource?.toLowerCase() !== 'production'
                        );
                        const bugIds = new Set();
                        internalBugs.forEach(bug => {
                          const id = bug.id?.toString() || bug._id?.toString();
                          if (id) bugIds.add(id);
                        });
                        bugsFromWorkItems.forEach(bug => {
                          const id = bug.id?.toString() || bug._id?.toString();
                          if (id) bugIds.add(id);
                        });
                        return bugIds.size;
                      })()}
                    </Typography>
                    <Typography variant="subtitle1">Internal Bugs</Typography>
                  </Paper>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      minWidth: '150px', 
                      flex: 1, 
                      bgcolor: '#e1f5fe', 
                      textAlign: 'center',
                      cursor: uniqueRepositories.length > 0 ? 'pointer' : 'default',
                      '&:hover': uniqueRepositories.length > 0 ? { bgcolor: '#b3e5fc' } : {}
                    }}
                    onClick={uniqueRepositories.length > 0 ? handleRepositoryCountClick : undefined}
                  >
                    <Typography variant="h4" color="info.main">
                      {uniqueRepositories.length}
                    </Typography>
                    <Typography variant="subtitle1">Repositories</Typography>
                  </Paper>
                  <Paper elevation={3} sx={{ p: 2, minWidth: '150px', flex: 1, bgcolor: '#f1f8e9', textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {relevantPRs.length}
                    </Typography>
                    <Typography variant="subtitle1">Relevant PRs</Typography>
                  </Paper>
                </Box>
              </Paper>
            </Box>
            
            {/* Filters Section */}
            {(workItems.length > 0 || productionBugs.length > 0 || internalBugs.length > 0) && (
              <Box mb={4}>
                <Paper elevation={2} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Filters
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="work-item-type-filter-label">Work Item Type</InputLabel>
                        <Select
                          labelId="work-item-type-filter-label"
                          value={workItemTypeFilter}
                          label="Work Item Type"
                          onChange={(e) => setWorkItemTypeFilter(e.target.value)}
                        >
                          <MenuItem value="">
                            <em>All Types</em>
                          </MenuItem>
                          <MenuItem value="Epic">Epic</MenuItem>
                          <MenuItem value="Feature">Feature</MenuItem>
                          <MenuItem value="Product Backlog Item">Product Backlog Item</MenuItem>
                          <MenuItem value="Task">Task</MenuItem>
                          <MenuItem value="Bug">Bug</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="state-filter-label">State</InputLabel>
                        <Select
                          labelId="state-filter-label"
                          value={stateFilter}
                          label="State"
                          onChange={(e) => setStateFilter(e.target.value)}
                        >
                          <MenuItem value="">
                            <em>All States</em>
                          </MenuItem>
                          {(() => {
                            // Get unique states from all items
                            const allStates = new Set();
                            workItems.forEach(item => {
                              if (item.state) allStates.add(item.state);
                            });
                            productionBugs.forEach(bug => {
                              if (bug.state) allStates.add(bug.state);
                            });
                            internalBugs.forEach(bug => {
                              if (bug.state) allStates.add(bug.state);
                            });
                            return Array.from(allStates).sort().map(state => (
                              <MenuItem key={state} value={state}>{state}</MenuItem>
                            ));
                          })()}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showOnlyWithPRs}
                            onChange={(e) => setShowOnlyWithPRs(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Show only items with PRs"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showItemsWithoutParent}
                            onChange={(e) => setShowItemsWithoutParent(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Show only items without parent"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )}
            
            {/* Feature Changes Section */}
            {(() => {
              // Filter out bugs from workItems - they're shown in separate sections
              const nonBugWorkItems = workItems.filter(item => item.workItemType !== 'Bug');
              return nonBugWorkItems.length > 0 && (
                <Box mb={4}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => setFeatureChangesExpanded(!featureChangesExpanded)}
                    >
                      <Typography variant="h5">
                        Feature Changes
                      </Typography>
                      <IconButton size="small">
                        {featureChangesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    <Collapse in={featureChangesExpanded}>
                      <Box sx={{ mt: 2 }}>
                        <FeatureChangesTable 
                          workItems={nonBugWorkItems} 
                          fixVersion={selectedFixVersion}
                          prsByWorkItem={prsByWorkItem}
                          pullRequests={pullRequests}
                          onPRCountClick={handlePRCountClick}
                          workItemTypeFilter={workItemTypeFilter}
                          stateFilter={stateFilter}
                          showOnlyWithPRs={showOnlyWithPRs}
                          showItemsWithoutParent={showItemsWithoutParent}
                        />
                      </Box>
                    </Collapse>
                  </Paper>
                </Box>
              );
            })()}
            
            {/* Production Bugs Section */}
            <Box mb={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => setProductionBugsExpanded(!productionBugsExpanded)}
                >
                  <Typography variant="h5">
                    Production Bugs
                  </Typography>
                  <IconButton size="small">
                    {productionBugsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={productionBugsExpanded}>
                  <Box sx={{ mt: 2 }}>
                    {(() => {
                      // Combine bugs from ProductionBug collection and WorkItem collection where issueSource == 'Production'
                      // Deduplicate by ID to avoid showing the same bug twice
                      const bugsFromWorkItems = workItems.filter(item => 
                        item.workItemType === 'Bug' && 
                        (item.issueSource === 'Production' || item.issueSource?.toLowerCase() === 'production')
                      );
                      
                      // Create a map to deduplicate by ID
                      const bugMap = new Map();
                      
                      // Add bugs from ProductionBug collection first
                      productionBugs.forEach(bug => {
                        const id = bug.id?.toString() || bug._id?.toString();
                        if (id && !bugMap.has(id)) {
                          bugMap.set(id, bug);
                        }
                      });
                      
                      // Add bugs from WorkItem collection, skipping duplicates
                      bugsFromWorkItems.forEach(bug => {
                        const id = bug.id?.toString() || bug._id?.toString();
                        if (id && !bugMap.has(id)) {
                          bugMap.set(id, bug);
                        }
                      });
                      
                      const allProductionBugs = Array.from(bugMap.values());
                      
                      return allProductionBugs.length > 0 ? (
                        <BugsTable
                          bugs={allProductionBugs}
                          bugType="production"
                          prsByWorkItem={prsByWorkItem}
                          pullRequests={pullRequests}
                          onPRCountClick={handlePRCountClick}
                          showItemsWithoutParent={showItemsWithoutParent}
                          workItemTypeFilter={workItemTypeFilter}
                          stateFilter={stateFilter}
                          showOnlyWithPRs={showOnlyWithPRs}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No production bugs found for this release.
                        </Typography>
                      );
                    })()}
                  </Box>
                </Collapse>
              </Paper>
            </Box>
            
            {/* Internal Bugs Section */}
            <Box mb={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => setInternalBugsExpanded(!internalBugsExpanded)}
                >
                  <Typography variant="h5">
                    Internal Bugs
                  </Typography>
                  <IconButton size="small">
                    {internalBugsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={internalBugsExpanded}>
                  <Box sx={{ mt: 2 }}>
                    {(() => {
                      // Combine bugs from InternalBug collection and WorkItem collection where issueSource != 'Production'
                      // Deduplicate by ID to avoid showing the same bug twice
                      const bugsFromWorkItems = workItems.filter(item => 
                        item.workItemType === 'Bug' && 
                        item.issueSource !== 'Production' && 
                        item.issueSource?.toLowerCase() !== 'production'
                      );
                      
                      // Create a map to deduplicate by ID
                      const bugMap = new Map();
                      
                      // Add bugs from InternalBug collection first
                      internalBugs.forEach(bug => {
                        const id = bug.id?.toString() || bug._id?.toString();
                        if (id && !bugMap.has(id)) {
                          bugMap.set(id, bug);
                        }
                      });
                      
                      // Add bugs from WorkItem collection, skipping duplicates
                      bugsFromWorkItems.forEach(bug => {
                        const id = bug.id?.toString() || bug._id?.toString();
                        if (id && !bugMap.has(id)) {
                          bugMap.set(id, bug);
                        }
                      });
                      
                      const allInternalBugs = Array.from(bugMap.values());
                      
                      return allInternalBugs.length > 0 ? (
                        <BugsTable
                          bugs={allInternalBugs}
                          bugType="internal"
                          prsByWorkItem={prsByWorkItem}
                          pullRequests={pullRequests}
                          onPRCountClick={handlePRCountClick}
                          showItemsWithoutParent={showItemsWithoutParent}
                          workItemTypeFilter={workItemTypeFilter}
                          stateFilter={stateFilter}
                          showOnlyWithPRs={showOnlyWithPRs}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No internal bugs found for this release.
                        </Typography>
                      );
                    })()}
                  </Box>
                </Collapse>
              </Paper>
            </Box>
          </>
        )
      ) : (
        <Typography variant="body2" color="textSecondary" align="center">
          Select a release to view work items and bugs.
        </Typography>
      )}
    </>
  );
  
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Releases
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View work items and bugs by release (fix version)
        </Typography>
      </Box>
      
      {loading ? (
        <Loading message="Loading fix versions..." />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="fullWidth"
            >
              <Tab label="Release Content" />
              <Tab label="Test Execution" />
              <Tab label="Trends - Tests & Bugs" />
              <Tab label="QA Report" />
              <Tab label="Release - QA Bugs" />
              <Tab label="Release Sign Off" />
              <Tab label="Release Status" />
            </Tabs>
          </Box>
          
          {/* Tab Content */}
          {tabValue === 0 && renderReleaseContent()}
          {tabValue === 1 && <ReleasesTab />}
          {tabValue === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                  <ReleaseHealthCharts />
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <BugComparisonCharts />
                </Paper>
              </Grid>
            </Grid>
          )}
          {tabValue === 3 && <QAReportTab fixVersion={selectedFixVersion} />}
          {tabValue === 4 && (() => {
            const internalBugsColumnsToHide = new Set([
              'System.AreaPath',
              'System.IterationPath',
              'System.CreatedBy',
              'System.AssignedTo',
              'Microsoft.VSTS.Common.ClosedDate',
              'System.CreatedDate',
              'System.Tags',
              'Microsoft.VSTS.Common.ResolvedDate',
              'Microsoft.VSTS.Common.Severity',
              'System.Description',
              'System.ChangedDate',
              'System.TeamProject'
            ]);
            const priorityRef = 'Microsoft.VSTS.Common.Priority';
            const filtered = (adoQueryData?.columns || []).filter((c) => !internalBugsColumnsToHide.has(c));
            const internalBugsColumns = (() => {
              if (!filtered.includes(priorityRef)) return filtered;
              const without = filtered.filter((c) => c !== priorityRef);
              return [...without.slice(0, 2), priorityRef, ...without.slice(2)];
            })();
            const getColumnLabel = (col) => {
              const c = (col || '').toLowerCase();
              if (c.endsWith('fix_version_dummy')) return 'Fix Version';
              if (c.endsWith('affects_version_dummy')) return 'Affect Version';
              if (c === 'system.workitemtype') return 'Type';
              return col.replace(/^System\.|^Microsoft\.VSTS\.Common\.|^Custom\./i, '').replace(/([A-Z])/g, ' $1').trim() || col;
            };
            const stripHtml = (str) => (str == null ? '' : String(str).replace(/<[^>]*>/g, ''));
            const handleAdoQuerySort = (col) => {
              if (col === adoQuerySortColumn) {
                setAdoQuerySortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
              } else {
                setAdoQuerySortColumn(col);
                setAdoQuerySortDirection('asc');
              }
            };
            const getSortValue = (row, col) => {
              const raw = row[col];
              if (raw == null) return '';
              if (col === 'System.Id' || col === 'System.Parent') {
                const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
                return Number.isNaN(n) ? String(raw) : n;
              }
              return col === 'System.Title' ? stripHtml(raw) : String(raw);
            };
            const sortedRows = (() => {
              const rows = adoQueryData?.rows || [];
              if (!adoQuerySortColumn || !internalBugsColumns.includes(adoQuerySortColumn)) return rows;
              const dir = adoQuerySortDirection === 'asc' ? 1 : -1;
              return [...rows].sort((a, b) => {
                const va = getSortValue(a, adoQuerySortColumn);
                const vb = getSortValue(b, adoQuerySortColumn);
                const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true });
                return dir * (cmp < 0 ? -1 : cmp > 0 ? 1 : 0);
              });
            })();
            const isBlank = (row, colKey) => {
              const v = row[colKey];
              if (v == null) return true;
              return String(v).trim() === '';
            };
            const isBlankOrNone = (row, colKey) => {
              const v = row[colKey];
              if (v == null) return true;
              const s = String(v).trim();
              return s === '' || s.toLowerCase() === 'none';
            };
            const parentCol = internalBugsColumns.find((c) => getColumnLabel(c) === 'Parent');
            const fixVersionCol = internalBugsColumns.find((c) => getColumnLabel(c) === 'Fix Version');
            const issueSourceCol = internalBugsColumns.find((c) => getColumnLabel(c) === 'Issue Source');
            const environmentCol = internalBugsColumns.find((c) => getColumnLabel(c) === 'Environment');
            const stateCol = internalBugsColumns.find((c) => getColumnLabel(c) === 'State');
            const teamCol = internalBugsColumns.find((c) => getColumnLabel(c) === 'Team');
            const filteredRows = sortedRows.filter((row) => {
              if (adoShowWithoutParent && parentCol && !isBlank(row, parentCol)) return false;
              if (adoShowWithoutFixVersion && fixVersionCol && !isBlankOrNone(row, fixVersionCol)) return false;
              if (adoShowWithoutIssueSource && issueSourceCol && !isBlank(row, issueSourceCol)) return false;
              if (adoShowWithoutEnvironment && environmentCol && !isBlank(row, environmentCol)) return false;
              if (adoExcludeClosed && stateCol) {
                const stateVal = row[stateCol];
                if (stateVal != null && String(stateVal).trim().toLowerCase() === 'closed') return false;
              }
              return true;
            });
            // Grouped counts for charts (by Team, Priority, State)
            const byTeam = {};
            const byPriority = {};
            const byState = {};
            filteredRows.forEach((row) => {
              const teamVal = teamCol != null && row[teamCol] != null ? String(row[teamCol]).trim() : '—';
              byTeam[teamVal] = (byTeam[teamVal] || 0) + 1;
              const priVal = row[priorityRef] != null ? String(row[priorityRef]).trim() : '—';
              byPriority[priVal] = (byPriority[priVal] || 0) + 1;
              const stateVal = stateCol != null && row[stateCol] != null ? String(row[stateCol]).trim() : '—';
              byState[stateVal] = (byState[stateVal] || 0) + 1;
            });
            const sortByLabelDesc = (a, b) => String(b.name).localeCompare(String(a.name), undefined, { numeric: true });
            const chartDataTeam = Object.entries(byTeam).map(([name, count]) => ({ name, count })).sort(sortByLabelDesc);
            const chartDataPriority = Object.entries(byPriority).map(([name, count]) => ({ name, count })).sort(sortByLabelDesc);
            const chartDataState = Object.entries(byState).map(([name, count]) => ({ name, count })).sort(sortByLabelDesc);
            const getAdoWorkItemUrl = (id) => {
              if (id == null || id === '') return null;
              const num = typeof id === 'number' ? id : parseInt(String(id), 10);
              if (Number.isNaN(num)) return null;
              const url = (adoQueryUrl || '').trim();
              const base = url.includes('/_queries') ? url.replace(/\/_queries.*$/, '') : (url.match(/^(https?:\/\/[^/]+(?:\/[^/]+)*)/)?.[1] || 'https://tr-legal.visualstudio.com/HighQ%20Tech');
              return `${base}/_workitems/edit/${num}`;
            };
            const getCellValue = (row, col) => {
              const raw = row[col];
              if (raw == null) return '';
              const s = String(raw);
              if (col === 'System.Title') return stripHtml(s);
              return s;
            };
            const isIdColumn = (col) => col === 'System.Id' || col === 'System.Parent';
            const renderCell = (row, col) => {
              if (isIdColumn(col)) {
                const raw = row[col];
                const id = raw != null && typeof raw === 'number' ? raw : (raw != null && /^\d+$/.test(String(raw)) ? String(raw) : null);
                if (id != null) {
                  const href = getAdoWorkItemUrl(id);
                  if (href) {
                    return (
                      <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                        {id}
                      </Link>
                    );
                  }
                }
              }
              return getCellValue(row, col);
            };
            const canEditAdoConfig = isAdmin();
            return (
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
              {isAuthenticated() && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    ADO Query Configuration
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="ADO Query URL"
                      value={adoQueryUrl}
                      onChange={(e) => { setAdoQueryUrl(e.target.value); setAdoQueryError(null); }}
                      placeholder="https://tr-legal.visualstudio.com/HighQ Tech/_queries/query/{queryId}/"
                      fullWidth
                      size="small"
                      sx={{ flex: '1 1 400px', maxWidth: 640 }}
                      helperText={canEditAdoConfig ? 'Paste the URL of your saved ADO query, then click Sync to load results' : 'Only admins can edit the query URL and run Sync.'}
                      disabled={!canEditAdoConfig}
                      InputProps={{ readOnly: !canEditAdoConfig }}
                    />
                    {canEditAdoConfig && (
                      <Button
                        variant="outlined"
                        startIcon={adoQueryLoading ? <CircularProgress size={20} /> : <SyncIcon />}
                        onClick={handleSyncAdoQuery}
                        disabled={adoQueryLoading || !adoQueryUrl.trim()}
                        sx={{ minWidth: 120 }}
                      >
                        {adoQueryLoading ? 'Syncing…' : 'Sync'}
                      </Button>
                    )}
                  </Box>
                  {adoQueryError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAdoQueryError(null)}>
                      {adoQueryError}
                    </Alert>
                  )}
                </>
              )}
              {adoQueryData && adoQueryData.rows && adoQueryData.rows.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={adoShowWithoutParent}
                            onChange={(e) => setAdoShowWithoutParent(e.target.checked)}
                          />
                        }
                        label="Show without parent"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={adoShowWithoutFixVersion}
                            onChange={(e) => setAdoShowWithoutFixVersion(e.target.checked)}
                          />
                        }
                        label="Show without Fix version"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={adoShowWithoutIssueSource}
                            onChange={(e) => setAdoShowWithoutIssueSource(e.target.checked)}
                          />
                        }
                        label="Show without Issue Source"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={adoShowWithoutEnvironment}
                            onChange={(e) => setAdoShowWithoutEnvironment(e.target.checked)}
                          />
                        }
                        label="Show without Environment"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={adoExcludeClosed}
                            onChange={(e) => setAdoExcludeClosed(e.target.checked)}
                          />
                        }
                        label="Exclude closed"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {filteredRows.length} / {sortedRows.length}
                    </Typography>
                  </Box>
                  {/* 3 data tables: Issues by Team, by Priority, by Status */}
                  {(() => {
                    const renderDataTable = (data, nameHeader) => {
                      if (!data || data.length === 0) return <Typography variant="body2" color="textSecondary">No data</Typography>;
                      return (
                        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Table size="small" sx={{ '& td, & th': { border: '1px solid', borderColor: 'divider' } }}>
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell sx={{ fontWeight: 700 }}>{nameHeader}</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">Count</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {data.map(({ name, count }) => (
                                <TableRow key={name}>
                                  <TableCell sx={{ border: '1px solid', borderColor: 'divider' }}>{name}</TableCell>
                                  <TableCell sx={{ border: '1px solid', borderColor: 'divider' }} align="right">{count}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      );
                    };
                    return (
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>Issues Grouped by Team</Typography>
                          {renderDataTable(chartDataTeam, 'Team')}
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>Issues Grouped by Priority</Typography>
                          {renderDataTable(chartDataPriority, 'Priority')}
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>Issues Grouped by Status</Typography>
                          {renderDataTable(chartDataState, 'Status')}
                        </Grid>
                      </Grid>
                    );
                  })()}
                  <TableContainer sx={{ maxHeight: 600, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {internalBugsColumns.map((col) => {
                            const label = getColumnLabel(col);
                            const isTitle = col === 'System.Title';
                            return (
                              <TableCell
                                key={col}
                                sortDirection={adoQuerySortColumn === col ? adoQuerySortDirection : false}
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: 'grey.200',
                                  ...(isTitle ? { minWidth: 160 } : { whiteSpace: 'nowrap' })
                                }}
                              >
                                <TableSortLabel
                                  active={adoQuerySortColumn === col}
                                  direction={adoQuerySortColumn === col ? adoQuerySortDirection : 'asc'}
                                  onClick={() => handleAdoQuerySort(col)}
                                >
                                  {label}
                                </TableSortLabel>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRows.map((row, idx) => (
                        <TableRow key={row['System.Id'] != null ? row['System.Id'] : idx}>
                          {internalBugsColumns.map((col) => {
                            const isTitle = col === 'System.Title';
                            return (
                              <TableCell
                                key={col}
                                sx={
                                  isTitle
                                    ? { minWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }
                                    : { whiteSpace: 'nowrap' }
                                }
                              >
                                {renderCell(row, col)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
              {adoQueryData && adoQueryData.rows && adoQueryData.rows.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  No work items returned by the query.
                </Typography>
              )}
            </Paper>
            );
          })()}
          {tabValue === 5 && (
            <>
              {/* Release selector — shared state with Release Content tab */}
              <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <FormControl sx={{ minWidth: 240 }}>
                  <InputLabel id="signoff-fix-version-select-label">Select Release (Fix Version)</InputLabel>
                  <Select
                    labelId="signoff-fix-version-select-label"
                    id="signoff-fix-version-select"
                    value={selectedFixVersion}
                    label="Select Release (Fix Version)"
                    onChange={handleFixVersionChange}
                  >
                    <MenuItem value="">
                      <em>Select a release</em>
                    </MenuItem>
                    {filteredFixVersions.map((version) => (
                      <MenuItem key={version} value={version}>
                        {version}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
              <ReleaseSignOffTab
                fixVersion={selectedFixVersion}
                releaseDetails={fixVersionDetails[selectedFixVersion] || null}
                productionBugs={productionBugs}
                internalBugs={internalBugs}
                pullRequests={pullRequests}
                prsByWorkItem={prsByWorkItem}
              />
            </>
          )}
          {tabValue === 6 && <ReleaseStatusTab fixVersion={selectedFixVersion} fixVersions={filteredFixVersions} />}
        </>
      )}

      {/* PR Details Dialog */}
      <Dialog 
        open={prDialogOpen} 
        onClose={() => setPrDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Pull Requests for {selectedWorkItemForPRs?.workItem?.title || `Work Item ${selectedWorkItemForPRs?.workItem?.id}`}
            </Typography>
            <IconButton onClick={() => setPrDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedWorkItemForPRs?.prs && selectedWorkItemForPRs.prs.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Repository</TableCell>
                    <TableCell>PR Number</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Target Branch</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Changes</TableCell>
                    <TableCell>Merged Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedWorkItemForPRs.prs.map((pr, index) => (
                    <TableRow key={`${pr.repository}-${pr.number}-${index}`}>
                      <TableCell>{pr.repository || 'Unknown'}</TableCell>
                      <TableCell>
                        <Link 
                          href={pr.url || pr.webUrl || '#'} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ color: '#1976d2' }}
                        >
                          #{pr.number}
                        </Link>
                      </TableCell>
                      <TableCell>{pr.title || 'N/A'}</TableCell>
                      <TableCell>{pr.baseBranch || 'Unknown'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={pr.status || pr.state || 'unknown'} 
                          size="small"
                          color={getPRStatusColor(pr.status || pr.state)}
                        />
                      </TableCell>
                      <TableCell>{pr.changedFiles || 0} files</TableCell>
                      <TableCell>
                        {pr.mergedAt ? formatDate(pr.mergedAt) : pr.merged ? 'Merged (date unknown)' : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No pull requests found for this work item.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Repository List Dialog */}
      <Dialog 
        open={repoDialogOpen} 
        onClose={() => setRepoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Repositories with Pull Requests ({repositories.length})
            </Typography>
            <IconButton onClick={() => setRepoDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {repositories.length > 0 ? (
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              {repositories.map((repo, index) => (
                <Box 
                  key={repo} 
                  sx={{ 
                    p: 1.5, 
                    borderBottom: index < repositories.length - 1 ? '1px solid #e0e0e0' : 'none',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <Typography variant="body1">{repo}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No repositories found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRepoDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Release Notes Editor Dialog */}
      <ReleaseNotesEditor
        open={releaseNotesEditorOpen}
        onClose={() => setReleaseNotesEditorOpen(false)}
        fixVersion={selectedFixVersion}
        items={releaseNotesItems}
        onApprove={handleApproveReleaseNotes}
        loading={generatingReleaseNotes}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default PublicReleasesView;

